/**
 * Hook that fetches team metadata for the chat empty-state display.
 * Accepts an optional pre-fetched Team object to skip the network request.
 *
 * @module hooks/use-team-info
 */
import { useEffect, useState } from "react";
import type { Team } from "@/types/team-types";
import { getPublicTeamInfo, fetchTeamById } from "@/services/teams-service";

/** Minimal team metadata needed for the chat empty state */
export interface TeamInfo {
  name: string;
  description: string | null;
  icon: string | null;
  samplePrompts?: string[];
  welcomeMessage?: string | null;
}

/**
 * Resolves team info from either a provided Team object or the API.
 *
 * @param teamId - The team ID to fetch info for (null to skip)
 * @param providedTeamInfo - Pre-fetched team data (skips API call when present)
 */
export function useTeamInfo(
  teamId: string | null,
  providedTeamInfo: Team | null = null,
): TeamInfo | null {
  const [teamInfo, setTeamInfo] = useState<TeamInfo | null>(null);

  useEffect(() => {
    if (providedTeamInfo && teamId) {
      const cfg = providedTeamInfo.patternConfig as Record<string, unknown> | undefined;
      setTeamInfo({
        name: providedTeamInfo.name || "",
        description: providedTeamInfo.description ?? null,
        icon: null,
        samplePrompts: Array.isArray(cfg?.samplePrompts) ? cfg.samplePrompts as string[] : undefined,
        welcomeMessage: typeof cfg?.welcomeMessage === "string" ? cfg.welcomeMessage : null,
      });
      return;
    }
    if (!teamId) return;

    let cancelled = false;

    const fetchTeamInfo = async () => {
      try {
        const data = await fetchTeamById(teamId);
        if (!cancelled && data) {
          const cfg = data.patternConfig as Record<string, unknown> | undefined;
          setTeamInfo({
            name: data.name,
            description: data.description ?? null,
            icon: data.icon ?? null,
            samplePrompts: Array.isArray(cfg?.samplePrompts) ? cfg.samplePrompts as string[] : undefined,
            welcomeMessage: typeof cfg?.welcomeMessage === "string" ? cfg.welcomeMessage : null,
          });
        }
        return;
      } catch {
        // Authenticated endpoint failed — fall through to public
      }

      try {
        const data = await getPublicTeamInfo(teamId);
        if (!cancelled && data) {
          setTeamInfo({
            name: data.name,
            description: data.description ?? null,
            icon: data.icon ?? null,
          });
        }
      } catch {
        // Team info not available — empty state will use generic heading
      }
    };

    fetchTeamInfo();
    return () => {
      cancelled = true;
    };
  }, [teamId, providedTeamInfo]);

  return teamInfo;
}
